import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Row, FormGroup, Label, Input } from "reactstrap";
import { DocumentTypeContext } from "../../context/DocumentContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";

type TemplateReplacerProps = {
  template: string;
  nameTemplate: string;
  openUseTemplate: { open: boolean; id: string };
};

const extractTagsFromTemplate = (html: string): string[] => {
  const regex = /{{(.*?)}}/g;
  const matches = html.match(regex);
  const cleanTags =
    matches?.map((tag) => tag.replace(/[{}]/g, "").trim()) || [];
  return Array.from(new Set(cleanTags));
};

export default function TemplateReplacer({
  template,
  nameTemplate,
  openUseTemplate,
}: TemplateReplacerProps) {
  const { setDocument, setNameDocument } = useContext(DocumentTypeContext);

  const [tags, setTags] = useState<string[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const navigate = useNavigate();
  const showToast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const foundTags = extractTagsFromTemplate(template);
    setTags(foundTags);

    const initialData: Record<string, string> = {};
    foundTags.forEach((tag) => {
      initialData[tag] = "";
    });

    setFormData(initialData);
    setPreviewHtml(template);
  }, [template]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    let updatedHtml = template;
    Object.entries(updatedFormData).forEach(([key, val]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      updatedHtml = updatedHtml.replace(regex, val || "");
    });
    setPreviewHtml(updatedHtml);
  };

  const toBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload    = () => resolve(reader.result as string);
      reader.onerror   = (err) => reject(err);
    });

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/templates/generate-pdf`,
        {
          htmlContent: previewHtml,
        },
        {
          responseType: "arraybuffer",
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("token")?.replace(/"/g, "") || ""
            }`,
          },
        }
      );

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });

      const base64pdf = await toBase64(pdfBlob);

      setDocument(base64pdf);
      setNameDocument(nameTemplate);
      navigate("/admin/add-signatories");
      setIsLoading(false);
    } catch (error) {
      console.log("Error generating PDF", error);
      setIsLoading(false);
      showToast("error", "Erro ao gerar PDF");
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h4 className="mb-4">
        Preenchimento de Template: <strong>{nameTemplate}</strong>
      </h4>

      <Row>
        <Col md="5">
          <div
            style={{
              background: "#fff",
              padding: "20px",
              border: "1px solid #eee",
              borderRadius: "8px",
            }}
          >
            <h6 className="mb-3 text-secondary">Preencha os campos</h6>

            {tags.map((tag) => (
              <FormGroup key={tag}>
                <Label for={tag}>{tag}</Label>
                <Input
                  type="text"
                  name={tag}
                  id={tag}
                  value={formData[tag] || ""}
                  onChange={handleChange}
                  placeholder={`Digite o valor para ${tag}`}
                />
              </FormGroup>
            ))}

            <Button
              color="primary"
              onClick={handleSubmit}
              block
              disabled={isLoading}
            >
              {isLoading ? "Salvando..." : "Salvar Preenchimento"}
            </Button>
          </div>
        </Col>

        <Col
          md="7"
          style={{
            background: "#f9f9f9",
            border: "1px solid #ddd",
            padding: "20px",
            borderRadius: "8px",
            overflowY: "auto",
          }}
        >
          <h6 className="mb-3 text-secondary">Visualização do Documento</h6>
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </Col>
      </Row>
    </div>
  );
}
